import { useEffect } from 'react';

import type { User } from '@/api/common/auth';
import { useCurrentVesselStore } from '@/modules/HSQE/stores/useCurrentVesselStore';

/**
 * Hydrate `useCurrentVesselStore` ONCE from the enriched whoami session
 * (spec section 5.3 / 6.2). The clamp is pre-applied in the session (section 6.6);
 * this never re-derives scope — it mirrors `Vessels[]` + `VesselScopeKind` +
 * `CurrentVesselId` straight into the store.
 *
 * Additive: when the session carries no HSQE vessel scope (`Vessels`/
 * `VesselScopeKind` absent) the store is left untouched, so non-HSQE sessions
 * are unaffected and the header switcher renders nothing.
 *
 * Called from the dedicated `HsqeProvider` (the HSQE-owned composition-root seam),
 * which self-sources the whoami `user`. Keeping this out of the shared `AuthProvider`/
 * `MockAuthProvider` is what lets those shared providers stay free of any `@/modules/HSQE`
 * import (F9/F10, ADR-0090).
 */
export const useHydrateVesselScope = (user: User | null | undefined): void => {
  useEffect(() => {
    if (user === null || user === undefined) return;

    const { Vessels, VesselScopeKind, CurrentVesselId } = user;
    if (Vessels === undefined || VesselScopeKind === undefined) return;

    useCurrentVesselStore.getState().hydrateFromSession(Vessels, VesselScopeKind, CurrentVesselId);
    // Key on the stable identity primitive `user?.Id`, NOT the whole `user` object: `useWhoAmI`
    // hands back a fresh object reference on every refetch, which re-fired this once-per-session
    // hydration (F9 / AUDIT M-2). `Vessels`/`VesselScopeKind`/`CurrentVesselId` are read as the
    // hydrated VALUES (referentially stable via TanStack structural sharing), not as deps.
  }, [user?.Id]);
};
